/**
 * Per-flow side-panel width persistence.
 *
 * Storage shape: a single localStorage key per flow under the
 * `qhs:side-panel-width:` namespace. Single-prefix layout makes
 * future bulk operations cheap (e.g., a debug "reset all panel
 * widths" action).
 *
 * All functions tolerate localStorage being unavailable (private
 * mode, blocked storage, quota exceeded) by silently falling back —
 * the panel's `width` prop default is the source of truth when
 * nothing is stored.
 */
const NAMESPACE = 'qhs:side-panel-width:';

const isStorageAvailable = () => {
  try {
    const probe = '__qhs_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch (_) {
    return false;
  }
};

export const readPersistedWidth = storageKey => {
  if (!storageKey || !isStorageAvailable()) return null;
  try {
    const raw = window.localStorage.getItem(NAMESPACE + storageKey);
    if (raw == null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch (_) {
    return null;
  }
};

export const writePersistedWidth = (storageKey, width) => {
  if (!storageKey || !isStorageAvailable()) return;
  if (!Number.isFinite(width) || width <= 0) return;
  try {
    window.localStorage.setItem(NAMESPACE + storageKey, String(Math.round(width)));
  } catch (_) {
    // QuotaExceeded etc. — silently no-op; panel still works.
  }
};

export const clearPersistedWidth = storageKey => {
  if (!storageKey || !isStorageAvailable()) return;
  try {
    window.localStorage.removeItem(NAMESPACE + storageKey);
  } catch (_) {
    // ignore
  }
};
